import React from 'react';
import {
  Card,
  CardMedia,
  CardContent,
  IconButton,
  Chip,
  Box,
  Typography,
  useTheme,
  Tooltip,
} from '@mui/material';
import {
  Favorite,
  FavoriteBorder,
  ShoppingCart,
  CompareArrows,
} from '@mui/icons-material';
import i18next from 'i18next';
import { useTranslation } from 'react-i18next';
import { useAddToCart } from '../../../../hooks/storefront/useAddToCart';
import { useToggleWishlistItem } from '../../../../hooks/customer/useToggleWishlistItem';

const ProductCard = ({
  product,
  onCompare,
  onClick,
  showActions = true,
  showBadge = true,
  ...props
}) => {
  const theme = useTheme();
  const { t } = useTranslation();
  const lng = i18next.language;
  const [isHovered, setIsHovered] = React.useState(false);
  const productId = Number(product?.id?.toString().split('-').pop());

  // Hooks
  const { mutate: addToCartMutate, isPending: isAdding } = useAddToCart();
  const { mutate: toggleWishlist, isPending: isTogglingWishlist } = useToggleWishlistItem();

  // Local state for immediate feedback - initialized from product prop
  const [inWishlist, setInWishlist] = React.useState(product?.is_wishlist || false);

  // Update local state when product prop changes
  React.useEffect(() => {
    setInWishlist(product?.is_wishlist || false);
  }, [product?.is_wishlist]);

  const handleCardClick = () => {
    if (onClick) onClick(product);
  };

  const handleCompare = (e) => {
    e.stopPropagation();
    if (onCompare) onCompare(product);
  };

  const handleAddToCart = (e) => {
    e.stopPropagation();
    addToCartMutate({
      product_id: productId,
      qty: 1
    });
  };

  const handleWishlist = (e) => {
    e.stopPropagation();
    toggleWishlist(productId, {
      onSuccess: (res) => {
        setInWishlist(res?.data?.data?.in_wishlist);
      }
    });
  };

  return (
    <Card
      sx={{
        cursor: 'pointer',
        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        border: `1px solid ${theme.palette.divider}`,
        borderRadius: 2,
        overflow: 'hidden',
        background: theme.palette.background.paper,
        position: 'relative',
        '&:hover': {
          transform: 'translateY(-4px)',
          boxShadow: `0 12px 24px ${theme.palette.primary.main}25`,
          borderColor: theme.palette.primary.main,
        },
        ...props.sx
      }}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      {/* Product Badge — discount % if on sale, otherwise "New" if recent */}
      {showBadge && (() => {
        const price = Number(product?.price);
        const compareAt = Number(product?.compare_at_price);
        const hasDiscount = Number.isFinite(price) && Number.isFinite(compareAt) && compareAt > price;
        const discountPct = hasDiscount ? Math.round(((compareAt - price) / compareAt) * 100) : 0;

        const createdAt = product?.created_at ? new Date(product.created_at) : null;
        const isRecent = createdAt && !Number.isNaN(createdAt.getTime())
          && (Date.now() - createdAt.getTime()) < 1000 * 60 * 60 * 24 * 30;

        let badgeLabel = null;
        let badgeBg = theme.palette.primary.main;
        if (hasDiscount) {
          badgeLabel = `-${discountPct}%`;
          badgeBg = theme.palette.error.main;
        } else if (isRecent) {
          badgeLabel = t('common.new');
        }

        if (!badgeLabel) return null;

        return (
          <Chip
            className="product-badge"
            label={badgeLabel}
            size="small"
            sx={{
              position: 'absolute',
              top: 12,
              left: 12,
              backgroundColor: badgeBg,
              color: 'white',
              fontWeight: 'bold',
              fontSize: '0.65rem',
              height: 20,
              transition: 'transform 0.3s ease',
              zIndex: 2,
            }}
          />
        );
      })()}

      {/* Overlay Actions */}
      {showActions && (
        <Box
          sx={{
            position: 'absolute',
            top: 12,
            right: lng === 'ar' ? 'auto' : 12,
            left: lng === 'ar' ? 12 : 'auto',
            display: 'flex',
            flexDirection: 'column',
            gap: 0.5,
            zIndex: 3,
            opacity: isHovered ? 1 : 0,
            transform: isHovered ? 'translateX(0)' : (lng === 'ar' ? 'translateX(20px)' : 'translateX(-20px)'),
            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
          }}
        >
          {/* Wishlist Button */}
          <Tooltip title={inWishlist ? t('product.removeFromWishlist') : t('product.addToWishlist')}>
            <IconButton
              size="small"
              onClick={handleWishlist}
              disabled={isTogglingWishlist}
              sx={{
                backgroundColor: inWishlist ? '#fff1f0' : 'rgba(255, 255, 255, 0.9)',
                backdropFilter: 'blur(10px)',
                color: inWishlist ? '#ff4d4f' : theme.palette.text.secondary,
                boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                '&:hover': {
                  backgroundColor: inWishlist ? '#fff1f0' : theme.palette.primary.main,
                  color: inWishlist ? '#ff4d4f' : 'white',
                  transform: 'scale(1.15)',
                },
                transition: 'all 0.3s cubic-bezier(0.175, 0.885, 0.32, 1.275)',
              }}
            >
              {inWishlist ? <Favorite fontSize="small" /> : <FavoriteBorder fontSize="small" />}
            </IconButton>
          </Tooltip>

          {/* Add to Cart Button */}
          <Tooltip title={t('product.addToCart')}>
            <IconButton
              size="small"
              onClick={handleAddToCart}
              disabled={isAdding}
              sx={{
                backgroundColor: 'rgba(255, 255, 255, 0.9)',
                backdropFilter: 'blur(10px)',
                color: theme.palette.text.secondary,
                boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                '&:hover': {
                  backgroundColor: theme.palette.primary.main,
                  color: 'white',
                  transform: 'scale(1.1)',
                },
                transition: 'all 0.2s ease',
              }}
            >
              <ShoppingCart fontSize="small" />
            </IconButton>
          </Tooltip>

          {/* Compare Button */}
          <Tooltip title={t('common.compare')}>
            <IconButton
              size="small"
              onClick={handleCompare}
              sx={{
                backgroundColor: 'rgba(255, 255, 255, 0.9)',
                backdropFilter: 'blur(10px)',
                color: theme.palette.text.secondary,
                boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                '&:hover': {
                  backgroundColor: theme.palette.primary.main,
                  color: 'white',
                  transform: 'scale(1.1)',
                },
                transition: 'all 0.2s ease',
              }}
            >
              <CompareArrows fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      )}

      {/* Product Image - Clickable Area */}
      <Box onClick={handleCardClick} sx={{ position: 'relative', overflow: 'hidden', cursor: 'pointer' }}>
        <CardMedia
          component="div"
          sx={{
            height: 240,
            backgroundColor: theme.palette.background.paper,
            backgroundImage: `url(${product.cover?.url || product.cover_url || product.image?.url || '/api/placeholder/300/240'})`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            backgroundRepeat: 'no-repeat',
            transition: 'transform 0.4s ease',
          }}
        />
      </Box>

      {/* Product Info - Clickable Area */}
      <CardContent onClick={handleCardClick} sx={{ flexGrow: 1, pb: 2, pt: 2, cursor: 'pointer' }}>
        <Typography
          variant="h6"
          fontWeight="600"
          sx={{
            mb: 1,
            height: 48,
            overflow: 'hidden',
            display: '-webkit-box',
            WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical',
            lineHeight: 1.3,
            color: theme.palette.text.primary,
            fontSize: '0.95rem',
          }}
        >
          {product.name?.[lng] || product.title?.[lng] || product.name || product.title || ''}
        </Typography>

        {/* Rating */}
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5 }}>
          {[...Array(5)].map((_, index) => (
            <Box
              key={index}
              component="span"
              sx={{
                fontSize: 12,
                color: index < 4 ? theme.palette.warning.main : theme.palette.action.disabled,
                mr: 0.25,
              }}
            >
              ★
            </Box>
          ))}
          <Typography variant="body2" color="text.secondary" sx={{ ml: 0.5, fontSize: '0.75rem' }}>
            ({product.rating || '4.0'})
          </Typography>
        </Box>

        {/* Price */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography
            variant="h6"
            color="primary.main"
            fontWeight="bold"
            sx={{ fontSize: '1.1rem' }}
          >
            {product.price} EGP
          </Typography>
          {product.compare_at_price && (
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ textDecoration: 'line-through', fontSize: '0.85rem' }}
            >
              {product.compare_at_price} EGP
            </Typography>
          )}
        </Box>
      </CardContent>
    </Card>
  );
};

export default ProductCard;
